import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import useRouteGate from '../../hooks/useRouteGate';
import AccessDeniedPanel from './AccessDeniedPanel';

/**
 * RouteGate — enforces the navigation registry on the ROUTE (RBAC Phase 3).
 *
 * Sits inside `ProtectedRoute`, so one edit covers all ~870 routes. The
 * proposal put a `<RequirePermission>` around each element; matching on the
 * current pathname instead means no route declaration has to be touched and no
 * new route can be added without a gate by forgetting the wrapper.
 *
 * **Refusing must not cost the user their sidebar.** Every route in `App.js` is
 * `<ProtectedRoute><SomeLayout><Page /></SomeLayout></ProtectedRoute>`, so on a
 * refusal we re-render the SAME layout element with the denial panel as its
 * children. The layout is identified by a static `isAppLayout` marker on the
 * component rather than by name — a build minifies `displayName` away, and
 * cloning a PAGE with new children would render the page anyway (a component
 * that ignores `children` still renders its own content), which would be a gate
 * that silently does nothing.
 *
 * **Not the security boundary.** `HasModuleAccess` still guards the data. This
 * turns a page that would render empty into a refusal that names the codename
 * to ask for.
 */
const RouteGate = ({ children }) => {
  const location = useLocation();
  const gate = useRouteGate(location.pathname);

  if (gate.status === 'allow') return children;

  const child = React.isValidElement(children) ? children : null;
  const isLayout = Boolean(child && child.type && child.type.isAppLayout);

  const content = gate.status === 'loading' ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
      <CircularProgress size={28} />
    </Box>
  ) : (
    <AccessDeniedPanel gate={gate.gate} path={gate.path} reason={gate.reason} />
  );

  // Keep the chrome: same layout element, different children.
  return isLayout ? React.cloneElement(child, {}, content) : content;
};

export default RouteGate;
